import { useTranslation } from 'react-i18next';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { GPUConfig } from '@utils/types/back-office/priority-queue';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  gpu: GPUConfig | undefined;
};

export function GpuConfigModal({ isOpen, onClose, gpu }: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const b = getColour('border');

  const stat = (label: string, value: unknown) => (
    <div>
      <div className="text-xs opacity-70 mb-1">{label}</div>
      <div className="text-base font-bold">{String(value ?? '-')}</div>
    </div>
  );

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onClose}
      size="x-large"
      title={t('gpu-configuration') || 'GPU Configuration'}
      confirmLabel={t('close') || 'Close'}
      cancelLabel={t('close') || 'Close'}
      destructive={false}
    >
      <div
        className="space-y-4 text-sm"
        style={{ color: getColour('normalText') }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="p-3 rounded-2xl border" style={{ borderColor: b }}>
            <div className="font-semibold opacity-70 mb-1">
              {t('enabled') || 'Enabled'}
            </div>
            <div className="text-base font-bold">
              {gpu?.enabled ? t('yes') || 'Yes' : t('no') || 'No'}
            </div>
          </div>
          <div className="p-3 rounded-2xl border" style={{ borderColor: b }}>
            <div className="font-semibold opacity-70 mb-1">
              {t('time-slicing') || 'Time Slicing'}
            </div>
            <div className="text-base font-bold">
              {gpu?.time_slicing ? t('yes') || 'Yes' : t('no') || 'No'}
            </div>
          </div>
        </div>

        <div className="p-3 rounded-2xl border" style={{ borderColor: b }}>
          <div className="font-semibold mb-3">
            {t('gpu-limits') || 'GPU Limits'}
          </div>
          <div className="grid grid-cols-3 gap-4">
            {stat(t('min-gpus') || 'Min GPUs', gpu?.min_gpus)}
            {stat(t('max-gpus') || 'Max GPUs', gpu?.max_gpus)}
            {stat(
              t('sessions-per-gpu') || 'Sessions/GPU',
              gpu?.sessions_per_gpu
            )}
          </div>
        </div>

        <div className="p-3 rounded-2xl border" style={{ borderColor: b }}>
          <div className="font-semibold mb-3">
            {t('capacity-management') || 'Capacity Management'}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {stat(
              t('capacity-gating') || 'Capacity Gating',
              gpu?.enable_capacity_gating ? t('enabled') : t('disabled')
            )}
            {stat(
              t('projected-capacity') || 'Projected Capacity',
              gpu?.enable_projected_capacity ? t('enabled') : t('disabled')
            )}
            {stat(
              t('projected-capacity-factor') || 'Projected Factor',
              gpu?.projected_capacity_factor
            )}
            {stat(
              t('capacity-buffer-factor') || 'Buffer Factor',
              gpu?.capacity_buffer_factor
            )}
          </div>
        </div>

        <div className="p-3 rounded-2xl border" style={{ borderColor: b }}>
          <div className="font-semibold mb-3">
            {t('queue-limits') || 'Queue Limits'}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {stat(
              t('max-queue-at-capacity') || 'Max Queue @ Capacity',
              gpu?.max_queue_size_at_capacity
            )}
            {stat(
              t('max-queue-age-alert') || 'Max Queue Age Alert',
              gpu?.max_queue_age_alert_seconds
                ? `${Math.floor(gpu.max_queue_age_alert_seconds / 60)}m`
                : '-'
            )}
          </div>
        </div>

        <div className="p-3 rounded-2xl border" style={{ borderColor: b }}>
          <div className="font-semibold mb-3">
            {t('scaling-config') || 'Scaling Configuration'}
          </div>
          <div className="grid grid-cols-3 gap-4">
            {stat(
              t('scale-up-eta') || 'Scale Up ETA',
              gpu?.scale_up_eta_minutes ? `${gpu.scale_up_eta_minutes}m` : '-'
            )}
            {stat(
              t('scale-down-delay') || 'Scale Down Delay',
              gpu?.scale_down_delay_seconds
                ? `${gpu.scale_down_delay_seconds}s`
                : '-'
            )}
            {stat(
              t('backpressure-sleep') || 'Backpressure Sleep',
              gpu?.backpressure_sleep_seconds
                ? `${gpu.backpressure_sleep_seconds}s`
                : '-'
            )}
          </div>
        </div>

        <div className="p-3 rounded-2xl border" style={{ borderColor: b }}>
          <div className="font-semibold mb-3">
            {t('wfq-config') || 'Weighted Fair Queuing (WFQ)'}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {stat(
              t('reference-priority') || 'Reference Priority',
              gpu?.wfq_reference_priority
            )}
            {stat(
              t('weight-divisor') || 'Weight Divisor',
              gpu?.wfq_weight_divisor
            )}
            {stat(t('min-weight') || 'Min Weight', gpu?.wfq_min_weight)}
            {stat(t('max-weight') || 'Max Weight', gpu?.wfq_max_weight)}
          </div>
        </div>
      </div>
    </ConfirmationModal>
  );
}
